import React from 'react';
import ReactDOM from 'react-dom';
import { HiOutlineDownload, HiOutlineDocumentText, HiOutlineViewList, HiArrowsExpand, HiOutlineAdjustments, HiX, HiZoomIn, HiZoomOut } from 'react-icons/hi';

export const CustomTable = ({ children, exportTableToCSV, copyTableToClipboard, setModalTableContent: _unused1, setIsModalOpen: _unused2, hideHeader = false, maxHeight, ...props }) => {
  const tableRef = React.useRef(null);
  const inlineDropdownRef = React.useRef(null);
  const modalDropdownRef = React.useRef(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [zoom, setZoom] = React.useState(1);
  const [columns, setColumns] = React.useState([]);
  const [visibleColumns, setVisibleColumns] = React.useState(new Set());
  const [isInlineColumnSelectorOpen, setIsInlineColumnSelectorOpen] = React.useState(false);
  const [isModalColumnSelectorOpen, setIsModalColumnSelectorOpen] = React.useState(false);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => setZoom(1);

  React.useEffect(() => {
    if (tableRef.current) {
      const headers = Array.from(tableRef.current.querySelectorAll('thead th'));
      const columnNames = headers.map(th => th.textContent.trim());
      setColumns(columnNames);
      setVisibleColumns(new Set(columnNames));
    }
  }, [children]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (inlineDropdownRef.current && !inlineDropdownRef.current.contains(event.target)) {
        setIsInlineColumnSelectorOpen(false);
      }
      if (modalDropdownRef.current && !modalDropdownRef.current.contains(event.target)) {
        setIsModalColumnSelectorOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleColumn = (columnName) => {
    setVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnName)) {
        // Don't allow hiding if it's the last visible column
        if (newSet.size > 1) {
          newSet.delete(columnName);
        }
      } else {
        newSet.add(columnName);
      }
      return newSet;
    });
  };

  const visibleColumnIndices = React.useMemo(() => 
    columns.map((col, i) => visibleColumns.has(col) ? i : -1).filter(i => i !== -1),
    [columns, visibleColumns]
  );
  
  const childrenWithColumnVisibility = React.useMemo(() => {
    if (columns.length === 0) return children;

    return React.Children.map(children, (child) => {
      if (child && (child.type === CustomTableHead || child.type === CustomTableBody)) {
        return React.cloneElement(child, {
          ...child.props,
          children: React.Children.map(child.props.children, (row) => {
            if (row && row.type === CustomTableRow) {
              return React.cloneElement(row, {
                ...row.props,
                children: React.Children.toArray(row.props.children).filter((_, index) => visibleColumnIndices.includes(index))
              });
            }
            return row;
          })
        });
      }
      return child;
    });
  }, [children, columns, visibleColumnIndices]);

  return (
    <div className="pm-table-wrapper w-full rounded-xl border border-gray-100 dark:border-gray-700/20 shadow-sm bg-white dark:bg-gray-900/30 backdrop-blur-sm my-4">
      {!hideHeader && (
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700/20 rounded-t-xl">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
            <HiOutlineViewList className="h-4 w-4" />
            Table
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsModalOpen(true)}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-md transition-all duration-200 ease-in-out"
              title="View Full Screen"
            >
              <HiArrowsExpand className="h-4 w-4" />
            </button>
            <div className="relative" ref={inlineDropdownRef}>
              <button
                onClick={() => setIsInlineColumnSelectorOpen(prev => !prev)}
                className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-md transition-all duration-200 ease-in-out"
                title="Select columns"
              >
                <HiOutlineAdjustments className="h-4 w-4" />
              </button>
              {isInlineColumnSelectorOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-20">
                  <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                    <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 font-semibold border-b border-gray-200 dark:border-gray-700">
                      Show/Hide Columns
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {columns.map((columnName) => (
                        <label key={columnName} className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            className="form-checkbox h-4 w-4 text-blue-600 dark:text-blue-500 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                            checked={visibleColumns.has(columnName)}
                            onChange={() => toggleColumn(columnName)}
                          />
                          <span className="ml-3 truncate" title={columnName}>{columnName}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button 
              onClick={() => tableRef.current && copyTableToClipboard(tableRef.current)} 
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-md transition-all duration-200 ease-in-out"
              title="Copy table data"
            >
              <HiOutlineDocumentText className="h-4 w-4" />
            </button>
            <button 
              onClick={() => tableRef.current && exportTableToCSV(tableRef.current)} 
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-md transition-all duration-200 ease-in-out"
              title="Export to CSV"
            >
              <HiOutlineDownload className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      <div className={`overflow-x-auto overflow-y-auto ${maxHeight ? '' : 'max-h-96'} border-collapse rounded-b-xl`} style={maxHeight ? { maxHeight } : undefined}>
        <table ref={tableRef} className="w-full border-collapse" {...props}>
          {childrenWithColumnVisibility}
        </table>
      </div>

      {isModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-90">
          <div className="relative max-w-full max-h-full p-4 w-full h-full">
            <div className="absolute top-4 left-4 right-4 z-10 bg-black bg-opacity-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white truncate">Table</h3>
                <div className="flex items-center gap-2">
                  <button onClick={handleZoomOut} className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all" title="Zoom out">
                    <HiZoomOut className="w-4 h-4" />
                  </button>
                  <span className="text-white text-sm px-2">{Math.round(zoom * 100)}%</span>
                  <button onClick={handleZoomIn} className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all" title="Zoom in">
                    <HiZoomIn className="w-4 h-4" />
                  </button>
                  <button onClick={handleResetZoom} className="text-white text-sm px-2 hover:bg-white hover:bg-opacity-20 rounded transition-all" title="Reset zoom">
                    Reset
                  </button>
                  <div className="relative" ref={modalDropdownRef}>
                    <button
                      onClick={() => setIsModalColumnSelectorOpen(prev => !prev)}
                      className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all"
                      title="Select columns"
                    >
                      <HiOutlineAdjustments className="w-4 h-4" />
                    </button>
                    {isModalColumnSelectorOpen && (
                      <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-20">
                        <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                          <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 font-semibold border-b border-gray-200 dark:border-gray-700">
                            Show/Hide Columns
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            {columns.map((columnName) => (
                              <label key={columnName} className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="form-checkbox h-4 w-4 text-blue-600 dark:text-blue-500 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                                  checked={visibleColumns.has(columnName)}
                                  onChange={() => toggleColumn(columnName)}
                                />
                                <span className="ml-3 truncate" title={columnName}>{columnName}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={() => tableRef.current && copyTableToClipboard(tableRef.current)} 
                    className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all"
                    title="Copy table data"
                  >
                    <HiOutlineDocumentText className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => tableRef.current && exportTableToCSV(tableRef.current)} 
                    className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all"
                    title="Export to CSV"
                  >
                    <HiOutlineDownload className="w-4 h-4" />
                  </button>
                  <button onClick={() => setIsModalOpen(false)} className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all">
                    <HiX className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-16 max-w-full max-h-full overflow-auto flex items-start justify-center" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
              <div className="w-[85%] mx-auto" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
                <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 6rem)' }}>
                  <CustomTable hideHeader maxHeight={'calc(100vh - 6rem)'} {...props}>
                    {childrenWithColumnVisibility}
                  </CustomTable>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export const CustomTableHead = ({ children, ...props }) => (
  <thead className="sticky top-0 z-10 border-b border-gray-100 dark:border-gray-700/20 bg-white dark:bg-tertiary-dark" {...props}>
    {children}
  </thead>
);

export const CustomTableBody = ({ children, ...props }) => (
  <tbody className="bg-white dark:bg-gray-900/30 divide-y divide-gray-100 dark:divide-gray-700/10" {...props}>
    {children}
  </tbody>
);

export const CustomTableRow = ({ children, ...props }) => (
  <tr className="bg-white dark:bg-tertiary-dark hover:bg-gray-50 dark:hover:bg-[#1f29374a] transition-colors duration-150 ease-in-out" {...props}>
    {children}
  </tr>
);

export const CustomTableHeader = ({ children, ...props }) => (
  <th className="sticky top-0 px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider z-10 bg-white dark:bg-tertiary-dark border-r !border-t-0 !border-b border-[#f3f4f6] dark:border-gray-700/20 last:border-r-0" {...props}>
    <div className="truncate">{children}</div>
  </th>
);

export const CustomTableCell = ({ children, ...props }) => (
  <td className="px-6 py-4 text-sm text-gray-800 dark:text-gray-200 border-t border-r border-[#f3f4f6] dark:border-gray-700/20 last:border-r-0" {...props}>
    <div className="break-words">{children}</div>
  </td>
);

